import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores';
import {
  LayoutDashboard,
  Users,
  FileText,
  Upload,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  BarChart3,
  TrendingUp,
  PieChart,
  School,
  Database,
  Shield,
  Bell,
  Search,
  ChevronDown,
  Activity,
  ClipboardCheck,
  MessageSquare,
  Palette,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { cn } from '../../lib/utils';

import { authApi } from '../../services/api';
import type { MenuItem as ApiMenuItem } from '../../types/common';
import { useTheme } from '../../hooks/useTheme';

// 图标映射表
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  FileText,
  Users,
  Upload,
  Settings,
  BarChart3,
  TrendingUp,
  PieChart,
  School,
  Database,
  Shield,
  Activity,
  ClipboardCheck,
  MessageSquare,
  Bell,
  Search,
};

interface MenuItem {
  key: string;
  path?: string;
  icon?: string;
  title: string;
  group?: string;
  children?: MenuItem[];
}

// 子菜单项组件
function SubMenuItem({ 
  child, 
  isActive,
  onClick 
}: { 
  child: { path?: string; title: string; icon?: string }; 
  isActive: boolean;
  onClick?: () => void;
}) {
  const Icon = child.icon ? iconMap[child.icon] : null;
  
  return (
    <NavLink
      to={child.path || '/'}
      onClick={onClick}
      className={cn(
        'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200',
        'hover:translate-x-0.5',
        isActive 
          ? 'bg-gradient-to-r from-cyan-100 to-blue-50 text-cyan-700 border-l-2 border-cyan-500' 
          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
      )}
    >
      {Icon && (
        <Icon className={cn(
          'w-4 h-4 transition-all duration-200',
          isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
        )} />
      )}
      <span className="font-medium">{child.title}</span>
      
      {isActive && (
        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
      )}
    </NavLink>
  );
}

// 父菜单项组件
function ParentMenuItem({ 
  item, 
  isCollapsed,
  isMobile,
  onNavigate 
}: { 
  item: MenuItem; 
  isCollapsed: boolean;
  isMobile?: boolean;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const [showTooltip, setShowTooltip] = useState(false);
  
  const visibleChildren = item.children || [];
  
  if (visibleChildren.length === 0) return null;
  
  const hasActiveChild = visibleChildren.some(child => 
    location.pathname === child.path || (child.path && location.pathname.startsWith(child.path + '/'))
  );
  
  const [expanded, setExpanded] = useState(hasActiveChild);
  
  useEffect(() => {
    if (hasActiveChild) {
      setExpanded(true);
    }
  }, [location.pathname, hasActiveChild]);

  // 折叠状态 - 悬浮面板
  if (isCollapsed && !isMobile) {
    return (
      <div 
        className="relative"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div className={cn(
          'flex items-center justify-center p-3 rounded-xl transition-all duration-200 cursor-pointer mx-2',
          'hover:bg-white/10 hover:scale-105',
          hasActiveChild 
            ? 'bg-gradient-to-br from-primary/20 to-primary/10 text-primary ring-1 ring-primary/30' 
            : 'text-muted-foreground hover:text-primary'
        )}>
          {item.icon && React.createElement(iconMap[item.icon] || LayoutDashboard, { className: 'w-5 h-5' })}
          {hasActiveChild && (
            <div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
          )}
        </div>
        
        {showTooltip && (
          <div className="absolute left-full top-0 ml-2 z-50 animate-in fade-in slide-in-from-left-2 duration-200">
            <div className="bg-popover border border-border rounded-xl shadow-xl p-3 min-w-[180px]">
              <div className="flex items-center gap-2 px-2 py-2 mb-2 border-b border-border/50">
                {item.icon && React.createElement(iconMap[item.icon] || LayoutDashboard, { className: 'w-4 h-4 text-cyan-400' })}
                <span className="text-popover-foreground font-semibold">{item.title}</span>
              </div>
              <div className="space-y-0.5">
                {visibleChildren.map(child => {
                  const isActive = location.pathname === child.path;
                  return (
                    <SubMenuItem
                      key={child.path}
                      child={child}
                      isActive={isActive}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-0.5 mx-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200',
          'hover:bg-muted',
          hasActiveChild 
            ? 'bg-gradient-to-r from-primary/10 to-transparent text-primary' 
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            'p-1.5 rounded-lg transition-all duration-200',
            hasActiveChild 
              ? 'bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/25' 
              : 'bg-muted'
          )}>
            {item.icon && React.createElement(iconMap[item.icon] || LayoutDashboard, { className: cn('w-4 h-4', hasActiveChild ? 'text-white' : 'text-muted-foreground') })}
          </div>
          <span className="font-medium">{item.title}</span>
        </div>
        
        <ChevronDown 
          className={cn(
            'w-4 h-4 text-slate-500 transition-transform duration-200',
            expanded && 'rotate-180 text-cyan-400'
          )} 
        />
      </button>
      
      <div className={cn(
        'overflow-hidden transition-all duration-200',
        expanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
      )}>
        <div className="ml-4 pl-3 border-l border-sidebar-border space-y-0.5 py-1">
          {visibleChildren.map(child => {
            const isActive = location.pathname === child.path;
            return (
              <SubMenuItem
                key={child.path}
                child={child}
                isActive={isActive}
                onClick={onNavigate}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

// 普通菜单项
function SimpleMenuItem({ 
  item, 
  isCollapsed,
  isMobile,
  onNavigate 
}: { 
  item: MenuItem; 
  isCollapsed: boolean;
  isMobile?: boolean;
  onNavigate?: () => void;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const Icon = item.icon ? (iconMap[item.icon] || LayoutDashboard) : LayoutDashboard;
  
  if (isCollapsed && !isMobile) {
    return (
      <div 
        className="relative mx-2"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <NavLink
          to={item.path || '/'}
          className={({ isActive }) =>
            cn(
              'flex items-center justify-center p-3 rounded-xl transition-all duration-200',
              'hover:scale-105',
              isActive
                ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30'
                : 'text-muted-foreground hover:bg-muted hover:text-primary'
            )
          }
        >
          <Icon className="w-5 h-5" />
        </NavLink>
        
        {showTooltip && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1.5 bg-popover text-popover-foreground text-sm rounded-lg whitespace-nowrap z-50 animate-in fade-in slide-in-from-left-2 duration-200 border border-border shadow-lg">
            {item.title}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.path || '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 mx-2',
          'hover:translate-x-0.5',
          isActive
            ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/30'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        )
      }
    >
      <div className="group-hover:scale-110 transition-transform duration-200">
        <Icon className="w-5 h-5" />
      </div>
      <span className="font-medium">{item.title}</span>
    </NavLink>
  );
}

// 用户头像
function UserAvatar({ user }: { user: any }) {
  const initials = user?.displayName?.charAt(0) || user?.username?.charAt(0) || 'U';
  
  return (
    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-600 p-0.5">
      <div className="w-full h-full rounded-full bg-background flex items-center justify-center">
        <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-br from-cyan-400 to-blue-400">
          {initials}
        </span>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);

  // 从后端 API 加载菜单
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const menus = await authApi.getMenu();
        setMenuItems(menus);
      } catch (error) {
        console.error('❌ 加载菜单失败:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const { theme, setTheme } = useTheme();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('已退出登录');
      navigate('/login');
    } catch (error) {
      toast.error('退出登录失败');
    }
  };

  const filteredMenuItems = menuItems;

  const currentPageTitle = filteredMenuItems.find(item => 
    item.path === location.pathname || 
    item.children?.some(child => child.path === location.pathname)
  )?.title || '工作台';

  return (
    <div className="flex h-screen bg-background">
      {/* 桌面端侧边栏 - 深色科技风 */}
      <aside
        className={cn(
          'hidden md:flex flex-col fixed left-0 top-0 h-full z-50',
          'bg-sidebar border-r border-sidebar-border',
          'transition-all duration-300 ease-out',
          sidebarCollapsed ? 'w-20' : 'w-64'
        )}
      >
        {/* Logo */}
        <div className={cn(
          'h-16 flex items-center border-b border-sidebar-border',
          sidebarCollapsed ? 'justify-center px-4' : 'px-5'
        )}>
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl blur-lg opacity-50" />
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-cyan-500/30 flex items-center justify-center">
                <span className="text-xs font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-slate-300">
                  UCAS
                </span>
              </div>
            </div>
            {!sidebarCollapsed && (
              <div className="flex flex-col">
                <span className="font-bold text-sidebar-foreground text-sm leading-tight">国科大官微</span>
                <span className="text-[10px] text-cyan-500 tracking-wider">DATA PLATFORM</span>
              </div>
            )}
          </div>
        </div>

        {/* 导航菜单 */}
        <nav className="flex-1 overflow-y-auto py-4 space-y-1 scrollbar-thin scrollbar-thumb-cyan-500/20 scrollbar-track-transparent">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="px-2 space-y-1">
              {filteredMenuItems.map((item, index) => {
                const prevGroup = index > 0 ? filteredMenuItems[index - 1]?.group : undefined;
                const showGroup = item.group && item.group !== prevGroup;
                return (
                  <div key={item.key}>
                    {showGroup && !sidebarCollapsed && (
                      <div className="px-5 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mt-4">
                        {item.group}
                      </div>
                    )}
                    {item.children && item.children.length > 0 ? (
                      <ParentMenuItem item={item} isCollapsed={sidebarCollapsed} />
                    ) : (
                      <SimpleMenuItem item={item} isCollapsed={sidebarCollapsed} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </nav>

        {/* 底部操作区 */}
        <div className="border-t border-sidebar-border p-3 space-y-2">
          {/* 用户信息 */}
          {!sidebarCollapsed && user && (
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-muted hover:bg-accent transition-colors">
              <UserAvatar user={user} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                  {user.displayName || user.username}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user.email || '管理员'}
                </p>
              </div>
            </div>
          )}
          
          {/* 操作按钮 */}
          <div className="flex gap-2">
            <button
              onClick={handleLogout}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
              title="退出登录"
            >
              <LogOut className="w-4 h-4" />
              {!sidebarCollapsed && <span className="text-sm font-medium">退出</span>}
            </button>
            
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="flex items-center justify-center p-2.5 rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
              title={sidebarCollapsed ? '展开' : '收起'}
            >
              <Menu className={cn(
                'w-4 h-4 transition-transform duration-300',
                sidebarCollapsed && 'rotate-180'
              )} />
            </button>
          </div>
        </div>
      </aside>

      {/* 移动端遮罩 */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* 移动端侧边栏 */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 bg-sidebar',
          'border-r border-sidebar-border transform transition-transform duration-300 md:hidden',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-sidebar-border">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl blur-lg opacity-50" />
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-cyan-500/30 flex items-center justify-center">
                <span className="text-xs font-black text-transparent bg-clip-text bg-gradient-to-b from-white to-slate-300">
                  UCAS
                </span>
              </div>
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-sidebar-foreground text-sm">国科大官微</span>
              <span className="text-[10px] text-cyan-400 tracking-wider">DATA PLATFORM</span>
            </div>
          </div>
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {filteredMenuItems.map((item) => (
            <div key={item.path}>
              {item.children ? (
                <ParentMenuItem 
                  item={item} 
                  isCollapsed={false} 
                  isMobile={true}
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              ) : (
                <SimpleMenuItem 
                  item={item} 
                  isCollapsed={false} 
                  isMobile={true}
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              )}
            </div>
          ))}
        </nav>
      </aside>

      {/* 主内容区 */}
      <div className={cn(
        'flex-1 flex flex-col min-w-0 transition-all duration-300',
        sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'
      )}>
        {/* 顶部导航栏 */}
        <header className="h-16 bg-header border-b border-sidebar-border flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button
              className="md:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* 面包屑 */}
            <nav className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">首页</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground/60" />
              <span className="text-header-foreground font-medium">{currentPageTitle}</span>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {/* 搜索 */}
            <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary text-secondary-foreground hover:bg-accent transition-colors cursor-pointer border border-border">
              <Search className="w-4 h-4" />
              <span className="text-sm">搜索...</span>
              <kbd className="hidden lg:inline-flex px-2 py-0.5 text-xs bg-background rounded text-muted-foreground border border-border">⌘K</kbd>
            </div>
            
            {/* 主题切换 */}
            <div className="w-32">
              <Select value={theme} onValueChange={(v) => setTheme(v as any)}>
                <SelectTrigger className="h-9 text-sm">
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-muted-foreground" />
                    <SelectValue placeholder="选择主题" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">浅色办公</SelectItem>
                  <SelectItem value="light-contrast">高对比浅色</SelectItem>
                  <SelectItem value="dark-tech">深色科技</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 通知 */}
            <button className="relative p-2.5 rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-cyan-500 shadow-[0_0_8px_rgba(6,182,212,0.8)]" />
            </button>
            
            {/* 用户头像 */}
            <div className="hidden sm:block">
              <UserAvatar user={user} />
            </div>
          </div>
        </header>

        {/* 页面内容 */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
